import { browserHistory } from 'react-router';
import find from 'lodash/find';
import findIndex from 'lodash/findIndex';
import isEqual from 'lodash/isEqual';
import moment from 'moment';
import qs from 'query-string';

import ApiService from '../util/api';
import { detectMobileDevice } from '../util/detectBrowser';
import { trackOptimizelyEvent, trackGoogleAnalyticsEvent } from '../util/trackingHelper';
import getTaskCompletedMsg from '../util/getTaskCompletedMsg';

import * as routingSelectors from '../selectors/routingSelectors';
import * as checklistSelectors from '../selectors/checklist/checklistSelectors';
import * as NotificationActions from './NotificationActions';
import * as ActionType from './types/ChecklistActionTypes';

export const CHECKLIST_URL = '/wedding/manage/wedding-checklist';

function requestChecklist() {
  return {
    type: ActionType.REQUEST_CHECKLIST,
  };
}

function receiveChecklist(json) {
  return {
    type: ActionType.RECEIVE_CHECKLIST,
    payload: {
      checklist: json,
    },
  };
}

export function resetChecklist() {
  return {
    type: ActionType.RESET_CHECKLIST,
  };
}

export function receiveChecklistFiltered(json) {
  return {
    type: ActionType.RECEIVE_CHECKLIST_FILTERED,
    payload: {
      checklist: json,
    },
  };
}
export function receiveChecklistFilteredGroups(json) {
  return {
    type: ActionType.RECEIVE_CHECKLIST_FILTERED_GROUPS,
    payload: {
      checklist: json,
    },
  };
}

export function selectTask(taskId) {
  return {
    type: ActionType.SELECT_TASK,
    payload: {
      id: taskId,
    },
  };
}

export function unselectTask() {
  return {
    type: ActionType.UNSELECT_TASK,
  };
}

export function addTask() {
  return {
    type: ActionType.ADD_TASK,
  };
}

function updateTask() {
  return {
    type: ActionType.UPDATE_TASK,
  };
}

export function receiveTask(json) {
  return {
    type: ActionType.RECEIVE_TASK,
    payload: {
      task: json,
    },
  };
}

export const toggleTaskFilter = filterValue => ({
  type: ActionType.TOGGLE_CHECKLIST_FILTER,
  payload: {
    filterValue,
  },
});

export const replaceTaskFilters = filterValues => ({
  type: ActionType.REPLACE_CHECKLIST_FILTERS,
  payload: {
    filterValues,
  },
});

export const clearFilters = () => ({ type: ActionType.CLEAR_CHECKLIST_FILTER });

export const removeTask = taskId => ({
  type: ActionType.REMOVE_TASK,
  payload: {
    id: taskId,
  },
});

export const trackChecklistAction = ({ eventName, taskId, customLabel }) => (dispatch, getState) => {
  const payload = {
    category: 'Wedding Checklist',
  };

  if (taskId && !customLabel) {
    const isCustomTask = checklistSelectors.getIsCustomTask(getState(), taskId);
    payload.label = isCustomTask ? 'User Created' : 'Template Task';
  }

  if (customLabel) {
    payload.label = customLabel;
  }

  return trackGoogleAnalyticsEvent(eventName, payload);
};

export const fetchChecklist = (setLoadingState = true) => (dispatch) => {
  if (setLoadingState) dispatch(requestChecklist());

  return ApiService.get('/web-api/v1/checklist')
    .then(json => dispatch(receiveChecklist(json)))
    .catch((err) => {
      dispatch(resetChecklist({}));
      throw new Error((err && err.message) || 'Sorry, there was a problem with your request');
    });
};

export const handleMobileClose = () => (dispatch, getState) => {
  const existingUrlSearch = routingSelectors.getRoutingSearch(getState());

  dispatch(unselectTask());
  browserHistory.push(`${CHECKLIST_URL}${existingUrlSearch}`);
};

export const goToChecklistTask = taskId => (dispatch, getState) => {
  const existingUrlSearch = routingSelectors.getRoutingSearch(getState());

  dispatch(selectTask(taskId));
  browserHistory.push(`${CHECKLIST_URL}/${taskId}${existingUrlSearch}`);
};

export const setFirstTaskAsActive = () => (dispatch, getState) => {
  const state = getState();
  const soonestTask = checklistSelectors.getSoonestChecklistTask(state);
  const taskId = soonestTask && soonestTask.id;

  dispatch(selectTask(taskId));
  browserHistory.push(`${CHECKLIST_URL}/${taskId}`);
};

// only to be used on desktop
export const setFirstFilteredTaskActive = () => (dispatch, getState) => {
  const state = getState();
  const firstResultId = checklistSelectors.getFirstFilterResult(state);
  const existingUrlSearch = routingSelectors.getRoutingSearch(state);

  if (!firstResultId) return;

  dispatch(selectTask(firstResultId));
  browserHistory.push(`${CHECKLIST_URL}/${firstResultId}${existingUrlSearch}`);
};

export const handleGoToPreviousTask = currentTaskId => (dispatch, getState) => {
  // todo: this will change once we get immutable keys from the BE
  if (currentTaskId === 'new') return;
  const state = getState();
  const currentTask = checklistSelectors.getChecklistTaskById(state, currentTaskId);
  const checklistTaskGroups = checklistSelectors.getChecklistTaskGroups(state);
  const checklistGroup = find(checklistTaskGroups, group => group.label === currentTask.group);
  const existingUrlSearch = routingSelectors.getRoutingSearch(state);

  if (!checklistGroup) return;

  const currentTaskIndex = checklistGroup.taskIds.indexOf(parseInt(currentTaskId, 10));
  const isFirstTaskInGroup = currentTaskIndex === 0;
  let prevId = null;

  if (isFirstTaskInGroup) {
    const groupIndex = findIndex(checklistTaskGroups, group => group.label === currentTask.group);
    const prevGroup = checklistTaskGroups[groupIndex - 1];

    if (!prevGroup) return;

    prevId = prevGroup.taskIds[prevGroup.taskIds.length - 1];
  } else {
    prevId = checklistGroup.taskIds[currentTaskIndex - 1];
  }

  if (prevId) {
    dispatch(selectTask(prevId));
    browserHistory.push(`${CHECKLIST_URL}/${prevId}${existingUrlSearch}`);
  }
};
export const handleGoToNextTask = currentTaskId => (dispatch, getState) => {
  // todo: this will change once we get immutable keys from the BE
  if (currentTaskId === 'new') return;
  const state = getState();
  const currentTask = checklistSelectors.getChecklistTaskById(state, currentTaskId);
  const checklistTaskGroups = checklistSelectors.getChecklistTaskGroups(state);
  const checklistGroup = find(checklistTaskGroups, group => group.label === currentTask.group);
  const existingUrlSearch = routingSelectors.getRoutingSearch(state);

  if (!checklistGroup) return;

  const currentTaskIndex = checklistGroup.taskIds.indexOf(parseInt(currentTaskId, 10));
  const isLastTaskInGroup = currentTaskIndex === checklistGroup.taskIds.length - 1;
  let nextId = null;

  if (isLastTaskInGroup) {
    const groupIndex = findIndex(checklistTaskGroups, group => group.label === currentTask.group);
    const nextGroup = checklistTaskGroups[groupIndex + 1];

    if (!nextGroup) return;

    [nextId] = nextGroup.taskIds;
  } else {
    nextId = checklistGroup.taskIds[currentTaskIndex + 1];
  }

  if (nextId) {
    dispatch(selectTask(nextId));
    browserHistory.push(`${CHECKLIST_URL}/${nextId}${existingUrlSearch}`);
  }
};

export const goToAddTask = () => (dispatch) => {
  dispatch(unselectTask());
  browserHistory.push(`${CHECKLIST_URL}/new`);
};

// only used on desktop. on mobile, we push back to the list view
export const handleNavigatingAfterDelete = taskId => (dispatch, getState) => {
  const isLastTask = checklistSelectors.getIsLastTask(getState(), taskId);

  if (isLastTask) {
    dispatch(handleGoToPreviousTask(taskId));
    return;
  }

  dispatch(handleGoToNextTask(taskId));
};

export function createTask(createTaskRequest) {
  return (dispatch) => {
    dispatch(addTask());
    dispatch(trackChecklistAction({ eventName: 'Task Added' }));

    const finalRequest = { ...createTaskRequest, due_at: moment.utc(createTaskRequest.due_at) };
    return ApiService.post('/web-api/v1/checklist/task', finalRequest).then((json) => {
      dispatch(receiveTask(json));
      return Promise.resolve(json);
    });
  };
}

export function editTask(editTaskRequest) {
  return (dispatch) => {
    dispatch(updateTask());

    const finalRequest = { ...editTaskRequest, due_at: moment.utc(editTaskRequest.due_at) };

    return ApiService.post(`/web-api/v1/checklist/task/id/${finalRequest.id}`, finalRequest)
      .then(json => dispatch(receiveTask(json)))
      .catch((err) => {
        throw new Error((err && err.message) || 'Sorry, there was a problem with your request');
      });
  };
}

export const editCustomTask = (formValues, taskId) => (dispatch, getState) => {
  // eslint-disable-next-line camelcase
  const { title: titleFromForm, notes: notesFromForm, due_at: dueAtFromForm } = formValues;
  const task = checklistSelectors.getChecklistTaskById(getState(), taskId);

  // since this is a PUT, pass existing data so it's more of a PATCH
  const title = titleFromForm || task.title;
  const id = parseInt(taskId, 10);
  const dueAt = dueAtFromForm || task.due_at;
  const notes = notesFromForm || task.notes;

  const body = Object.assign(
    {},
    // eslint-disable-next-line camelcase
    { id, title, due_at: dueAt, notes },
  );

  dispatch(trackChecklistAction({ eventName: 'Task Edited', taskId }));

  return dispatch(editTask(body));
};

export const assignPartnerToTask = (taskId, partnerObjectId) => (dispatch, getState) => {
  const task = checklistSelectors.getChecklistTaskById(getState(), taskId);
  const alreadyAssigned = task.assigned_user_object_id === partnerObjectId;
  const body = {
    id: taskId,
    title: task.title,
    due_at: task.due_at,
    notes: task.notes,
    assigned_user_object_id: alreadyAssigned ? null : partnerObjectId,
  };

  return dispatch(editTask(body));
};

export function toggleCompleted(taskId) {
  return (dispatch, getState) => {
    const state = getState();
    const task = checklistSelectors.getChecklistTaskById(state, taskId);
    const request = { ...task };

    request.completed = !(request.completed_at !== null);

    if (request.completed) {
      dispatch(trackChecklistAction({ eventName: 'Task Completed', taskId }));
      trackOptimizelyEvent('Checklist Task Marked as Complete');
    }
    return dispatch(editTask(request));
  };
}

export const emitConfMsg = isTaskCompleted => dispatch => dispatch(NotificationActions.confirmation({ message: isTaskCompleted ? getTaskCompletedMsg() : 'Task Updated' }));

export const addOrRemoveFilterFromURL = (filters = []) => {
  if (!filters.length) {
    browserHistory.push(CHECKLIST_URL);
    return;
  }

  const filterStr = encodeURIComponent(filters.join(','));
  browserHistory.push(`${CHECKLIST_URL}?q=${filterStr}`);
};

export const getEncodedFilterQueryString = (filters = []) => {
  if (!filters.length) {
    return '';
  }
  const filterStr = encodeURIComponent(filters.join(','));
  return `?q=${filterStr}`;
};

export const clearFiltersAndResetURL = () => (dispatch) => {
  const isMobile = detectMobileDevice();

  dispatch(clearFilters());
  addOrRemoveFilterFromURL();

  if (!isMobile) dispatch(setFirstTaskAsActive());
};

export const filterTasks = () => (dispatch, getState) => {
  const filters = checklistSelectors.getTaskFilters(getState());
  dispatch(requestChecklist());
  dispatch(trackChecklistAction({ eventName: 'Checklist Filtered', customLabel: filters.join(',') }));

  return ApiService.post('/web-api/v2/checklist/task/filter', { filters }).then((json) => {
    dispatch(receiveChecklist(json));
    dispatch(receiveChecklistFiltered(json));
    dispatch(receiveChecklistFilteredGroups(json));
    return json;
  });
};

// Sync filters in URL to ones in the store. This can occur when navigating forward/back in the browser
export const filterAndSyncFiltersInURL = () => (dispatch, getState) => {
  const filters = checklistSelectors.getTaskFilters(getState());
  let { q: filtersInURL } = qs.parse(window.location.search);

  if (filtersInURL) filtersInURL = filtersInURL.split(',');

  if (!filtersInURL.length) {
    dispatch(clearFilters());
  } else if (!isEqual(filters, filtersInURL)) {
    dispatch(replaceTaskFilters(filtersInURL));
  }

  dispatch(filterTasks()).then(() => {
    const isMobile = detectMobileDevice();

    if (!isMobile) {
      dispatch(setFirstFilteredTaskActive());
    }
  });
};

export const handleToggleTaskFilter = filterValue => (dispatch, getState) => {
  dispatch(toggleTaskFilter(filterValue));
  dispatch(unselectTask());

  const filters = checklistSelectors.getTaskFilters(getState());

  if (!filters.length) {
    dispatch(clearFilters());
    dispatch(setFirstTaskAsActive());
    return;
  }

  /* We don't call filterTasks here because that's handled in
     the container component which calls filterAndSyncFiltersInURL */
  addOrRemoveFilterFromURL(filters);
};

export const handleApplyFilters = () => (dispatch, getState) => {
  const filters = checklistSelectors.getTaskFilters(getState());
  addOrRemoveFilterFromURL(filters);
};

export const maybeFilterTasks = () => (dispatch, getState) => {
  const filtersActive = checklistSelectors.getTaskFilters(getState());

  if (filtersActive.length) dispatch(filterTasks());
};

export const toggleInvitePartnerView = () => (dispatch, getState) => {
  const toggledValue = !checklistSelectors.getPartnerDropdownState(getState());

  if (toggledValue) dispatch(trackChecklistAction({ eventName: 'Invite Partner Clicked' }));

  return dispatch({
    type: ActionType.TOGGLE_INVITE_PARTNER_VIEW,
    payload: {
      invitePartnerViewActive: toggledValue,
    },
  });
};

export const deleteTask = taskId => (dispatch) => {
  dispatch(trackChecklistAction({ eventName: 'Task Deleted', taskId }));

  return ApiService.post(`/web-api/v1/checklist/task/id/${taskId}/delete`)
    .then(() => dispatch(removeTask(taskId)))
    .then(() => dispatch(NotificationActions.confirmation({ message: 'Task Deleted' })));
};



// WEBPACK FOOTER //
// ./src/actions/ChecklistV2Actions.js